'use client';

import { useMemo, useState } from 'react';
import { parseHex, evaluateWcag, formatRgbString } from '@/lib/browser-color-tools-extra';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

function normalizeHex(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0.35rem 0.65rem',
        borderRadius: '999px',
        fontSize: '0.85rem',
        fontWeight: 600,
        color: ok ? '#0f5132' : '#842029',
        background: ok ? 'rgba(25, 135, 84, 0.15)' : 'rgba(220, 53, 69, 0.15)',
        border: `1px solid ${ok ? 'rgba(25, 135, 84, 0.4)' : 'rgba(220, 53, 69, 0.4)'}`,
      }}
    >
      {ok ? '✓' : '✕'} {label}
    </span>
  );
}

export default function BrowserColorContrastChecker() {
  const [foreground, setForeground] = useState('#1A1A1A');
  const [background, setBackground] = useState('#FFFFFF');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const fgRgb = useMemo(() => parseHex(foreground), [foreground]);
  const bgRgb = useMemo(() => parseHex(background), [background]);

  const result = useMemo(() => {
    if (!fgRgb || !bgRgb) return null;
    return evaluateWcag(fgRgb, bgRgb);
  }, [fgRgb, bgRgb]);

  const previewFg = fgRgb ? formatRgbString(fgRgb) : '#1A1A1A';
  const previewBg = bgRgb ? formatRgbString(bgRgb) : '#FFFFFF';

  async function handleCopyRatio() {
    if (!result) return;
    try {
      await copyText(result.ratioLabel);
      setCopyMessage('Contrast ratio copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  function handleSwap() {
    setForeground(background);
    setBackground(foreground);
  }

  const invalid = !fgRgb || !bgRgb;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Accessibility</span>
        <h2 className="tool-live-title">Color contrast checker</h2>
        <p className="tool-live-subtitle">
          Check any foreground and background pair against WCAG AA and AAA, live in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Text (foreground)</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  aria-label="Foreground color picker"
                  onChange={(e) => setForeground(e.target.value)}
                  style={{
                    width: '3rem',
                    height: '2.5rem',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                  type="color"
                  value={foreground.match(/^#[0-9a-fA-F]{6}$/) ? foreground : '#000000'}
                />
                <input
                  onChange={(e) => setForeground(normalizeHex(e.target.value))}
                  placeholder="#1A1A1A"
                  type="text"
                  value={foreground}
                />
              </div>
            </label>

            <label className="tool-live-field">
              <span>Background</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  aria-label="Background color picker"
                  onChange={(e) => setBackground(e.target.value)}
                  style={{
                    width: '3rem',
                    height: '2.5rem',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                  type="color"
                  value={background.match(/^#[0-9a-fA-F]{6}$/) ? background : '#FFFFFF'}
                />
                <input
                  onChange={(e) => setBackground(normalizeHex(e.target.value))}
                  placeholder="#FFFFFF"
                  type="text"
                  value={background}
                />
              </div>
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleSwap} type="button">
              Swap colors
            </button>
            <button
              className="button button-secondary"
              disabled={!result}
              onClick={handleCopyRatio}
              type="button"
            >
              Copy ratio
            </button>
          </div>

          {invalid ? (
            <p className="status-message" style={{ color: '#dc3545' }}>
              Enter valid 6-digit hex colors (for example #1A1A1A).
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              background: previewBg,
              color: previewFg,
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <p style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem' }}>
              Large sample text
            </p>
            <p style={{ fontSize: '1rem', margin: 0 }}>
              The quick brown fox jumps over the lazy dog. Normal body copy at this size needs a
              4.5:1 ratio to pass WCAG AA.
            </p>
          </div>

          {result ? (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '2rem', fontWeight: 800, margin: '0 0 0.75rem' }}>
                {result.ratioLabel}
              </p>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: '9rem', fontWeight: 600 }}>AA normal (4.5:1)</span>
                  <Badge label={result.aaNormal ? 'Pass' : 'Fail'} ok={result.aaNormal} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: '9rem', fontWeight: 600 }}>AA large (3:1)</span>
                  <Badge label={result.aaLarge ? 'Pass' : 'Fail'} ok={result.aaLarge} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: '9rem', fontWeight: 600 }}>AAA normal (7:1)</span>
                  <Badge label={result.aaaNormal ? 'Pass' : 'Fail'} ok={result.aaaNormal} />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ minWidth: '9rem', fontWeight: 600 }}>AAA large (4.5:1)</span>
                  <Badge label={result.aaaLarge ? 'Pass' : 'Fail'} ok={result.aaaLarge} />
                </div>
              </div>
            </div>
          ) : null}
          <p className="tool-live-panel-note">Computed locally. Large text = 18pt bold or 24pt.</p>
        </section>
      </div>
    </div>
  );
}
